import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  User, History, Shield, AlertTriangle, LogOut,
  BarChart2, Calendar, Clock, ChevronRight, Edit2, Check,
  X, Eye, EyeOff, AlertCircle, CheckCircle, Zap, Crown,
  Lock, Trash2, BarChart, Activity
} from 'lucide-react';
import { API_URL } from '../styles/shared';

// ─── Tier Config ──────────────────────────────────────────────────────────────
const TIERS = {
  free:    { label: 'Free',    color: '#64748b', bg: 'rgba(100,116,139,0.12)', limit: 5,    icon: <BarChart size={12} /> },
  basic:   { label: 'Basic',   color: '#00d4ff', bg: 'rgba(0,212,255,0.12)',   limit: 20,   icon: <Zap size={12} /> },
  premium: { label: 'Premium', color: '#00ff88', bg: 'rgba(0,255,136,0.12)',   limit: 9999, icon: <Crown size={12} /> },
};

// ─── Sidebar Nav Items ────────────────────────────────────────────────────────
const NAV = [
  { id: 'overview',  label: 'Overview',  icon: User },
  { id: 'history',   label: 'History',   icon: History },
  { id: 'security',  label: 'Security',  icon: Shield },
  { id: 'account',   label: 'Account',   icon: AlertTriangle },
];

// ─── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, sub, color = '#00d4ff', icon }) => (
  <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '12px', padding: '1.25rem 1.5rem', position: 'relative', overflow: 'hidden' }}>
    <div style={{ position: 'absolute', top: 0, left: 0, width: '3px', height: '100%', background: color, borderRadius: '3px 0 0 3px' }} />
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
      <span style={{ fontSize: '0.72rem', color: '#475569', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{label}</span>
      {icon && <span style={{ color, opacity: 0.6 }}>{icon}</span>}
    </div>
    <div style={{ fontSize: '1.75rem', fontWeight: 900, color: '#f8fafc', letterSpacing: '-0.03em', lineHeight: 1 }}>{value}</div>
    {sub && <div style={{ fontSize: '0.75rem', color: '#475569', marginTop: '0.4rem' }}>{sub}</div>}
  </div>
);

// ─── Usage Bar ────────────────────────────────────────────────────────────────
const UsageBar = ({ used, limit, color }) => {
  const pct = limit >= 9999 ? 100 : Math.min((used / limit) * 100, 100);
  const isUnlimited = limit >= 9999;
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
        <span style={{ fontSize: '0.78rem', color: '#64748b' }}>Monthly analyses used</span>
        <span style={{ fontSize: '0.78rem', fontWeight: 700, color }}>
          {used} / {isUnlimited ? '∞' : limit}
        </span>
      </div>
      <div style={{ height: '6px', background: 'rgba(255,255,255,0.06)', borderRadius: '3px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${isUnlimited ? 60 : pct}%`, background: pct > 80 ? '#ffaa00' : color, borderRadius: '3px', transition: 'width 0.8s ease' }} />
      </div>
      {!isUnlimited && <div style={{ fontSize: '0.72rem', color: '#475569', marginTop: '0.4rem' }}>{limit - used} remaining this month</div>}
    </div>
  );
};

// ─── Main ProfilePage ─────────────────────────────────────────────────────────
const ProfilePage = ({ user, token, onLogout, onPasswordChanged, onAccountDeleted }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [displayName, setDisplayName] = useState(user?.displayName || user?.email?.split('@')[0] || '');
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(displayName);
  const [nameSaving, setNameSaving] = useState(false);

  // History
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);

  // Security
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [pwMsg, setPwMsg] = useState('');

  // Account deletion
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteMsg, setDeleteMsg] = useState('');
  const [showDeleteForm, setShowDeleteForm] = useState(false);

  const tier = TIERS[user?.subscriptionTier] || TIERS.free;
  const used = user?.analysesThisMonth || 0;
  const joinDate = user?.createdAt ? new Date(user.createdAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'N/A';
  const initials = (user?.displayName || user?.email || 'U').slice(0, 2).toUpperCase();

  // Load history when tab opens
  useEffect(() => {
    if (activeTab === 'history') fetchHistory(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchHistory = async (page) => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/stocks/history?page=${page}&limit=10`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.success) {
        setHistory(res.data.data.analyses || []);
        setHistoryTotal(res.data.data.total || 0);
        setHistoryPage(page);
      }
    } catch (err) {
      console.error('History error:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSaveName = async () => {
    if (!nameInput.trim() || nameInput === displayName) { setEditingName(false); return; }
    setNameSaving(true);
    try {
      await axios.patch(`${API_URL}/api/auth/profile`, { displayName: nameInput.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      setDisplayName(nameInput.trim());
      setEditingName(false);
    } catch (err) {
      setNameInput(displayName);
      setEditingName(false);
    } finally {
      setNameSaving(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPwMsg('');
    if (newPw.length < 8) { setPwMsg('error:Password must be at least 8 characters'); return; }
    if (newPw !== confirmPw) { setPwMsg('error:Passwords do not match'); return; }
    setPwLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/change-password`, { currentPassword: currentPw, newPassword: newPw }, { headers: { Authorization: `Bearer ${token}` } });
      setPwMsg('success:Password updated successfully');
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
      onPasswordChanged && onPasswordChanged();
    } catch (err) {
      setPwMsg(`error:${err.response?.data?.message || 'Password change failed'}`);
    } finally {
      setPwLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'DELETE') { setDeleteMsg('error:Type DELETE to confirm'); return; }
    if (!deletePassword) { setDeleteMsg('error:Password is required to delete your account'); return; }
    setDeleteLoading(true);
    try {
      await axios.delete(`${API_URL}/api/auth/account`, { 
        headers: { Authorization: `Bearer ${token}` },
        data: { password: deletePassword }
      });
      onAccountDeleted && onAccountDeleted();
    } catch (err) {
      setDeleteMsg(`error:${err.response?.data?.message || 'Deletion failed'}`);
      setDeleteLoading(false);
    }
  };

  const getRec = (rec) => {
    const map = { BULLISH: { color: '#00ff88', bg: 'rgba(0,255,136,0.1)' }, BEARISH: { color: '#ff4444', bg: 'rgba(255,68,68,0.1)' }, NEUTRAL: { color: '#ffaa00', bg: 'rgba(255,170,0,0.1)' } };
    return map[rec] || { color: '#64748b', bg: 'rgba(100,116,139,0.1)' };
  };

  return (
    <div style={{ padding: '2rem 1rem', fontFamily: '"DM Sans", system-ui, sans-serif' }}>

      {/* ── Profile Header ── */}
      <div style={{ background: 'linear-gradient(135deg, rgba(12,25,50,0.9), rgba(18,35,65,0.8))', border: '1px solid rgba(0,212,255,0.1)', borderRadius: '20px', padding: '2rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
        {/* Avatar */}
        <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'linear-gradient(135deg, #00ff88, #00d4ff)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', fontWeight: 900, color: '#050d1a', flexShrink: 0, boxShadow: '0 0 0 4px rgba(0,212,255,0.15)' }}>
          {initials}
        </div>

        {/* Name & email */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.35rem', flexWrap: 'wrap' }}>
            {editingName ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input value={nameInput} onChange={e => setNameInput(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleSaveName()}
                  autoFocus maxLength={32}
                  style={{ background: 'rgba(10,20,45,0.8)', border: '1.5px solid #00d4ff', borderRadius: '8px', color: '#f8fafc', padding: '0.35rem 0.7rem', fontSize: '1.1rem', fontWeight: 700, outline: 'none', width: '180px', fontFamily: 'inherit' }} />
                <button onClick={handleSaveName} disabled={nameSaving} style={{ background: 'rgba(0,255,136,0.15)', border: '1px solid rgba(0,255,136,0.3)', borderRadius: '6px', color: '#00ff88', cursor: 'pointer', padding: '0.3rem' }}>
                  {nameSaving ? '...' : <Check size={14} />}
                </button>
                <button onClick={() => { setEditingName(false); setNameInput(displayName); }} style={{ background: 'rgba(255,68,68,0.1)', border: '1px solid rgba(255,68,68,0.2)', borderRadius: '6px', color: '#ff4444', cursor: 'pointer', padding: '0.3rem' }}>
                  <X size={14} />
                </button>
              </div>
            ) : (
              <>
                <h2 style={{ color: '#f8fafc', fontSize: '1.3rem', fontWeight: 900, margin: 0, letterSpacing: '-0.02em' }}>{displayName}</h2>
                <button onClick={() => { setEditingName(true); setNameInput(displayName); }} style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', padding: '0.2rem' }}>
                  <Edit2 size={13} />
                </button>
              </>
            )}
            {/* Tier badge */}
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.2rem 0.7rem', background: tier.bg, border: `1px solid ${tier.color}40`, borderRadius: '20px', fontSize: '0.7rem', fontWeight: 800, color: tier.color, letterSpacing: '0.06em' }}>
              {tier.icon} {tier.label.toUpperCase()}
            </span>
            {/* Verified badge */}
            {user?.emailVerified && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.2rem 0.7rem', background: 'rgba(0,255,136,0.08)', border: '1px solid rgba(0,255,136,0.2)', borderRadius: '20px', fontSize: '0.7rem', fontWeight: 700, color: '#00ff88' }}>
                <CheckCircle size={10} /> Verified
              </span>
            )}
          </div>
          <p style={{ color: '#64748b', margin: 0, fontSize: '0.875rem' }}>{user?.email}</p>
          <p style={{ color: '#334155', margin: '0.2rem 0 0', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
            <Calendar size={11} /> Member since {joinDate}
          </p>
        </div>

        {/* Logout */}
        <button onClick={onLogout} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1.2rem', background: 'rgba(255,68,68,0.08)', border: '1px solid rgba(255,68,68,0.2)', borderRadius: '10px', color: '#ff6b6b', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', fontFamily: 'inherit' }}>
          <LogOut size={14} /> Sign Out
        </button>
      </div>

      {/* ── Layout: Sidebar + Content ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '1.5rem', alignItems: 'start' }}>

        {/* Sidebar */}
        <div style={{ background: 'rgba(10,20,45,0.6)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '0.5rem', position: 'sticky', top: '1rem' }}>
          {NAV.map(item => {
            const Icon = item.icon;
            const active = activeTab === item.id;
            return (
              <button key={item.id} onClick={() => setActiveTab(item.id)}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', borderRadius: '10px', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontWeight: active ? 700 : 500, fontSize: '0.875rem', textAlign: 'left', transition: 'all 0.15s', background: active ? 'rgba(0,212,255,0.1)' : 'transparent', color: active ? '#00d4ff' : '#64748b', }}>
                <Icon size={16} />
                {item.label}
                {active && <ChevronRight size={14} style={{ marginLeft: 'auto' }} />}
              </button>
            );
          })}

          {/* Usage in sidebar */}
          <div style={{ margin: '0.75rem 0.5rem 0.5rem', paddingTop: '0.75rem', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
            <UsageBar used={used} limit={tier.limit} color={tier.color} />
            {user?.subscriptionTier === 'free' && (
              <button onClick={() => {}} style={{ width: '100%', marginTop: '0.75rem', padding: '0.6rem', background: 'linear-gradient(135deg,rgba(0,255,136,0.15),rgba(0,212,255,0.15))', border: '1px solid rgba(0,212,255,0.25)', borderRadius: '8px', color: '#00d4ff', fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer', fontFamily: 'inherit' }}>
                ⚡ Upgrade Plan
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div>

          {/* ── OVERVIEW TAB ── */}
          {activeTab === 'overview' && (
            <div>
              {user?.previousAccount && used >= 5 && (
                <div style={{ background: 'rgba(255,170,0,0.08)', border: '1px solid rgba(255,170,0,0.25)', borderRadius: '12px', padding: '1rem 1.25rem', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                    <span style={{ fontSize: '1.25rem' }}>👋</span>
                    <div>
                      <p style={{ color: '#ffaa00', fontWeight: 700, margin: '0 0 0.2rem', fontSize: '0.9rem' }}>Welcome back!</p>
                      <p style={{ color: '#94a3b8', margin: 0, fontSize: '0.82rem', lineHeight: 1.6 }}>Your account access has been restricted. Contact us at support@marketpulze.com for assistance.</p>
                    </div>
                  </div>
                  <button onClick={() => setActiveTab('account')} style={{ padding: '0.6rem 1.25rem', background: 'linear-gradient(135deg,#ffaa00,#ff8800)', border: 'none', borderRadius: '8px', color: '#050d1a', fontWeight: 800, fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 }}>
                    Upgrade Plan →
                  </button>
                </div>
              )}
              <h3 style={{ color: '#f8fafc', fontSize: '1rem', fontWeight: 800, margin: '0 0 1.25rem', letterSpacing: '-0.01em' }}>Account Overview</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                <StatCard label="Analyses This Month" value={used} sub={`of ${tier.limit >= 9999 ? '∞' : tier.limit} allowed`} color={tier.color} icon={<BarChart2 size={16} />} />
                <StatCard label="Plan Tier" value={tier.label} sub="Current subscription" color={tier.color} icon={tier.icon} />
                <StatCard label="Account Status" value={user?.emailVerified ? 'Verified' : 'Unverified'} sub="Email verification" color={user?.emailVerified ? '#00ff88' : '#ffaa00'} icon={<CheckCircle size={16} />} />
                <StatCard label="Member Since" value={joinDate.split(' ')[1] || 'N/A'} sub={joinDate.split(' ')[0] || ''} color="#00d4ff" icon={<Calendar size={16} />} />
              </div>

              {/* Account details card */}
              <div style={{ background: 'rgba(10,20,45,0.6)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', overflow: 'hidden' }}>
                <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Activity size={15} style={{ color: '#00d4ff' }} />
                  <span style={{ color: '#f8fafc', fontWeight: 700, fontSize: '0.875rem' }}>Account Details</span>
                </div>
                {[
                  { label: 'Email Address', value: user?.email },
                  { label: 'Display Name', value: displayName },
                  { label: 'Subscription', value: tier.label },
                  { label: 'Email Verified', value: user?.emailVerified ? '✅ Yes' : '⚠️ No — check inbox' },
                  { label: 'Member Since', value: joinDate },
                ].map((row, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.9rem 1.5rem', borderBottom: i < 4 ? '1px solid rgba(255,255,255,0.03)' : 'none' }}>
                    <span style={{ fontSize: '0.83rem', color: '#64748b' }}>{row.label}</span>
                    <span style={{ fontSize: '0.83rem', color: '#cbd5e1', fontWeight: 600 }}>{row.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── HISTORY TAB ── */}
          {activeTab === 'history' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
                <h3 style={{ color: '#f8fafc', fontSize: '1rem', fontWeight: 800, margin: 0 }}>Analysis History</h3>
                <span style={{ fontSize: '0.78rem', color: '#475569' }}>{historyTotal} total analyses</span>
              </div>

              {historyLoading ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#475569' }}>
                  <div style={{ width: 32, height: 32, border: '2px solid rgba(0,212,255,0.2)', borderTop: '2px solid #00d4ff', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 1rem' }} />
                  Loading history...
                </div>
              ) : history.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '4rem 2rem', background: 'rgba(10,20,45,0.4)', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <BarChart2 size={40} style={{ color: '#334155', margin: '0 auto 1rem', display: 'block' }} />
                  <p style={{ color: '#475569', margin: 0, fontWeight: 600 }}>No analyses yet</p>
                  <p style={{ color: '#334155', fontSize: '0.85rem', marginTop: '0.4rem' }}>Your analysis history will appear here</p>
                </div>
              ) : (
                <>
                  <div style={{ background: 'rgba(10,20,45,0.6)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', overflow: 'hidden' }}>
                    {/* Table header */}
                    <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr 80px 80px 120px', padding: '0.75rem 1.5rem', borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(0,0,0,0.2)' }}>
                      {['TICKER', 'COMPANY', 'SCORE', 'REC', 'DATE'].map(h => (
                        <span key={h} style={{ fontSize: '0.68rem', color: '#334155', fontWeight: 700, letterSpacing: '0.08em' }}>{h}</span>
                      ))}
                    </div>
                    {history.map((item, i) => {
                      const rec = getRec(item.recommendation);
                      return (
                        <div key={i} style={{ display: 'grid', gridTemplateColumns: '80px 1fr 80px 80px 120px', padding: '0.9rem 1.5rem', borderBottom: i < history.length - 1 ? '1px solid rgba(255,255,255,0.03)' : 'none', transition: 'background 0.15s' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,212,255,0.03)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <span style={{ fontWeight: 800, color: '#00d4ff', fontSize: '0.9rem', letterSpacing: '0.04em' }}>{item.ticker}</span>
                          <span style={{ color: '#94a3b8', fontSize: '0.83rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.companyName || '—'}</span>
                          <span style={{ fontWeight: 800, color: item.score >= 70 ? '#00ff88' : item.score >= 40 ? '#ffaa00' : '#ff4444', fontSize: '0.9rem' }}>{item.score}</span>
                          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0.2rem 0.5rem', background: rec.bg, borderRadius: '5px', fontSize: '0.7rem', fontWeight: 800, color: rec.color, letterSpacing: '0.04em', width: 'fit-content' }}>
                            {item.recommendation}
                          </span>
                          <span style={{ color: '#475569', fontSize: '0.78rem' }}>
                            {item.analyzedAt ? new Date(item.analyzedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  {/* Pagination */}
                  {historyTotal > 10 && (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginTop: '1rem' }}>
                      {Array.from({ length: Math.ceil(historyTotal / 10) }, (_, i) => i + 1).map(pg => (
                        <button key={pg} onClick={() => fetchHistory(pg)}
                          style={{ width: 32, height: 32, borderRadius: '8px', border: `1px solid ${pg === historyPage ? '#00d4ff' : 'rgba(255,255,255,0.08)'}`, background: pg === historyPage ? 'rgba(0,212,255,0.12)' : 'transparent', color: pg === historyPage ? '#00d4ff' : '#64748b', fontWeight: 700, cursor: 'pointer', fontSize: '0.83rem', fontFamily: 'inherit' }}>
                          {pg}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ── SECURITY TAB ── */}
          {activeTab === 'security' && (
            <div>
              <h3 style={{ color: '#f8fafc', fontSize: '1rem', fontWeight: 800, margin: '0 0 1.25rem' }}>Security Settings</h3>
              <div style={{ background: 'rgba(10,20,45,0.6)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '1.5rem', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem', paddingBottom: '1.25rem', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <div style={{ width: 40, height: 40, borderRadius: '10px', background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Lock size={18} style={{ color: '#00d4ff' }} />
                  </div>
                  <div>
                    <h4 style={{ color: '#f8fafc', margin: 0, fontSize: '0.95rem', fontWeight: 700 }}>Change Password</h4>
                    <p style={{ color: '#64748b', margin: 0, fontSize: '0.8rem' }}>Use a strong, unique password for your account</p>
                  </div>
                </div>

                <form onSubmit={handlePasswordChange} noValidate>
                  {[
                    { label: 'Current Password', val: currentPw, set: setCurrentPw, show: showCurrent, toggle: () => setShowCurrent(s => !s) },
                    { label: 'New Password', val: newPw, set: setNewPw, show: showNew, toggle: () => setShowNew(s => !s) },
                    { label: 'Confirm New Password', val: confirmPw, set: setConfirmPw, show: showNew, toggle: () => setShowNew(s => !s) },
                  ].map((field, i) => (
                    <div key={i} style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#64748b', marginBottom: '0.4rem', letterSpacing: '0.04em' }}>{field.label.toUpperCase()}</label>
                      <div style={{ position: 'relative' }}>
                        <input type={field.show ? 'text' : 'password'} value={field.val} onChange={e => field.set(e.target.value)}
                          style={{ width: '100%', padding: '0.8rem 3rem 0.8rem 1rem', background: 'rgba(10,20,45,0.8)', border: '1.5px solid rgba(255,255,255,0.08)', borderRadius: '10px', color: '#f8fafc', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />
                        <button type="button" onClick={field.toggle} style={{ position: 'absolute', right: '0.9rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#475569', cursor: 'pointer', padding: 0 }}>
                          {field.show ? <EyeOff size={15} /> : <Eye size={15} />}
                        </button>
                      </div>
                    </div>
                  ))}

                  {pwMsg && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1rem', borderRadius: '8px', marginBottom: '1rem', background: pwMsg.startsWith('success') ? 'rgba(0,255,136,0.08)' : 'rgba(255,68,68,0.08)', border: `1px solid ${pwMsg.startsWith('success') ? 'rgba(0,255,136,0.2)' : 'rgba(255,68,68,0.2)'}` }}>
                      {pwMsg.startsWith('success') ? <CheckCircle size={14} style={{ color: '#00ff88' }} /> : <AlertCircle size={14} style={{ color: '#ff4444' }} />}
                      <span style={{ fontSize: '0.83rem', color: pwMsg.startsWith('success') ? '#00ff88' : '#ff6b6b' }}>{pwMsg.split(':')[1]}</span>
                    </div>
                  )}

                  <button type="submit" disabled={pwLoading} style={{ padding: '0.8rem 1.75rem', background: 'linear-gradient(135deg,#00ff88,#00d4ff)', border: 'none', borderRadius: '10px', color: '#050d1a', fontWeight: 800, fontSize: '0.875rem', cursor: pwLoading ? 'not-allowed' : 'pointer', fontFamily: 'inherit', opacity: pwLoading ? 0.7 : 1 }}>
                    {pwLoading ? '⏳ Updating...' : 'Update Password'}
                  </button>
                </form>
              </div>

              {/* Sessions info */}
              <div style={{ background: 'rgba(0,212,255,0.04)', border: '1px solid rgba(0,212,255,0.1)', borderRadius: '12px', padding: '1rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <Clock size={16} style={{ color: '#00d4ff', flexShrink: 0 }} />
                <p style={{ color: '#64748b', fontSize: '0.8rem', margin: 0, lineHeight: 1.6 }}>
                  Your session is secured with JWT authentication. Changing your password will require you to sign in again on all devices.
                </p>
              </div>
            </div>
          )}

          {/* ── ACCOUNT TAB ── */}
          {activeTab === 'account' && (
            <div>
              <h3 style={{ color: '#f8fafc', fontSize: '1rem', fontWeight: 800, margin: '0 0 1.25rem' }}>Account Management</h3>

              {/* Subscription section */}
              <div style={{ background: 'rgba(10,20,45,0.6)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '1.5rem', marginBottom: '1.25rem' }}>
                <h4 style={{ color: '#f8fafc', margin: '0 0 1rem', fontSize: '0.9rem', fontWeight: 700 }}>Current Subscription</h4>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem', background: 'rgba(0,0,0,0.2)', borderRadius: '10px', border: `1px solid ${tier.color}20` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <span style={{ color: tier.color }}>{tier.icon}</span>
                    <div>
                      <p style={{ color: '#f8fafc', fontWeight: 800, margin: 0 }}>{tier.label} Plan</p>
                      <p style={{ color: '#64748b', fontSize: '0.78rem', margin: 0 }}>{tier.limit >= 9999 ? 'Unlimited analyses' : `${tier.limit} analyses per month`}</p>
                    </div>
                  </div>
                  {user?.subscriptionTier !== 'premium' && (
                    <button onClick={() => {}} style={{ padding: '0.6rem 1.2rem', background: 'linear-gradient(135deg,#00ff88,#00d4ff)', border: 'none', borderRadius: '8px', color: '#050d1a', fontWeight: 800, fontSize: '0.8rem', cursor: 'pointer', fontFamily: 'inherit' }}>
                      Upgrade →
                    </button>
                  )}
                </div>
              </div>

              {/* Danger Zone */}
              <div style={{ background: 'rgba(255,68,68,0.04)', border: '1px solid rgba(255,68,68,0.2)', borderRadius: '16px', padding: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1rem' }}>
                  <AlertTriangle size={16} style={{ color: '#ff4444' }} />
                  <h4 style={{ color: '#ff4444', margin: 0, fontSize: '0.9rem', fontWeight: 800 }}>Danger Zone</h4>
                </div>
                <p style={{ color: '#64748b', fontSize: '0.85rem', lineHeight: 1.7, marginBottom: '1.25rem' }}>
                  Permanently delete your account and all associated data. This action cannot be undone. Your email will be flagged to prevent immediate re-registration for abuse prevention.
                </p>

                {!showDeleteForm ? (
                  <button onClick={() => setShowDeleteForm(true)}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.7rem 1.25rem', background: 'rgba(255,68,68,0.08)', border: '1px solid rgba(255,68,68,0.3)', borderRadius: '10px', color: '#ff4444', fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer', fontFamily: 'inherit' }}>
                    <Trash2 size={15} /> Delete My Account
                  </button>
                ) : (
                  <div style={{ background: 'rgba(255,68,68,0.06)', borderRadius: '12px', padding: '1.25rem', border: '1px solid rgba(255,68,68,0.15)' }}>
                    <p style={{ color: '#f8fafc', fontWeight: 700, fontSize: '0.875rem', marginBottom: '0.75rem' }}>
                      Type <code style={{ background: 'rgba(255,68,68,0.15)', padding: '0.1rem 0.4rem', borderRadius: '4px', color: '#ff4444' }}>DELETE</code> to confirm:
                    </p>
                    <input value={deleteConfirm} onChange={e => setDeleteConfirm(e.target.value)} placeholder="Type DELETE here"
                      style={{ width: '100%', padding: '0.8rem 1rem', background: 'rgba(10,20,45,0.8)', border: `1.5px solid ${deleteConfirm === 'DELETE' ? '#ff4444' : 'rgba(255,68,68,0.2)'}`, borderRadius: '10px', color: '#f8fafc', fontSize: '0.9rem', outline: 'none', marginBottom: '0.75rem', boxSizing: 'border-box', fontFamily: 'inherit' }} />
                    <div style={{ marginBottom: '0.75rem' }}>
                      <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, color: '#94a3b8', marginBottom: '0.4rem', letterSpacing: '0.04em' }}>YOUR PASSWORD</label>
                      <input type="password" value={deletePassword} onChange={e => setDeletePassword(e.target.value)} placeholder="Enter your current password"
                        style={{ width: '100%', padding: '0.8rem 1rem', background: 'rgba(10,20,45,0.8)', border: '1.5px solid rgba(255,68,68,0.2)', borderRadius: '10px', color: '#f8fafc', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />
                    </div>
                    {deleteMsg && (
                      <p style={{ color: '#ff4444', fontSize: '0.8rem', marginBottom: '0.75rem' }}>{deleteMsg.split(':')[1]}</p>
                    )}
                    <div style={{ display: 'flex', gap: '0.75rem' }}>
                      <button onClick={handleDeleteAccount} disabled={deleteLoading || deleteConfirm !== 'DELETE'}
                        style={{ padding: '0.7rem 1.25rem', background: deleteConfirm === 'DELETE' ? '#ff4444' : 'rgba(255,68,68,0.2)', border: 'none', borderRadius: '10px', color: '#fff', fontWeight: 800, fontSize: '0.875rem', cursor: deleteConfirm === 'DELETE' ? 'pointer' : 'not-allowed', fontFamily: 'inherit' }}>
                        {deleteLoading ? '⏳ Deleting...' : 'Permanently Delete Account'}
                      </button>
                      <button onClick={() => { setShowDeleteForm(false); setDeleteConfirm(''); setDeletePassword(''); setDeleteMsg(''); }}
                        style={{ padding: '0.7rem 1.25rem', background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', color: '#64748b', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer', fontFamily: 'inherit' }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default ProfilePage;
